import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const RELATIONSHIP_TYPES = [
    { value: "owner", label: "Owner" },
    { value: "tenant", label: "Tenant" },
    { value: "family", label: "Family Member" },
    { value: "other", label: "Other Occupant" },
];

/** Handles both "Edit Relationship" (change relationship type) and "Mark Moved Out"
 * (state-changing, so it's a confirmation dialog, never a silent one-click action). */
export default function EditRelationshipDialog({ open, onOpenChange, relationship, mode, onSaved }) {
    const [relationshipType, setRelationshipType] = useState("family");
    const [moveOutDate, setMoveOutDate] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open || !relationship) return;
        setRelationshipType(relationship.relationship_type);
        setMoveOutDate(new Date().toISOString().slice(0, 10));
    }, [open, relationship]);

    async function saveRelationshipType() {
        setBusy(true);
        try {
            await api.patch(`/flat-residents/${relationship.relationship_id}`, { relationship_type: relationshipType });
            toast.success("Relationship updated");
            onOpenChange(false);
            onSaved?.();
        } catch (e) { toast.error(formatError(e)); }
        finally { setBusy(false); }
    }

    async function confirmMoveOut() {
        setBusy(true);
        try {
            await api.post(`/flat-residents/${relationship.relationship_id}/move-out`, { move_out_date: moveOutDate || null });
            toast.success(`${relationship.name} marked as moved out`);
            onOpenChange(false);
            onSaved?.();
        } catch (e) { toast.error(formatError(e)); }
        finally { setBusy(false); }
    }

    if (!relationship) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-sm max-w-md" data-testid="edit-relationship-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">
                        {mode === "moveout" ? "Mark Moved Out" : "Edit Relationship"}
                    </DialogTitle>
                </DialogHeader>

                {mode === "moveout" ? (
                    <div className="space-y-4">
                        <p className="text-sm text-brand-inkSoft">
                            <span className="font-medium text-brand-ink">{relationship.name}</span> will be removed from the active household
                            and marked as moved out. This preserves their historical record — it does not delete their resident profile.
                        </p>
                        <div className="space-y-2">
                            <Label>Move-out Date</Label>
                            <Input data-testid="move-out-date-input" type="date" value={moveOutDate} onChange={(e) => setMoveOutDate(e.target.value)} className="rounded-sm border-brand-line" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                            <Button type="button" data-testid="move-out-confirm-btn" disabled={busy} onClick={confirmMoveOut}
                                className="rounded-full bg-brand-action hover:bg-brand-actionHover">Confirm Moved Out</Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Relationship Type for {relationship.name}</Label>
                            <Select value={relationshipType} onValueChange={setRelationshipType}>
                                <SelectTrigger data-testid="edit-relationship-type-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {RELATIONSHIP_TYPES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Cancel</Button>
                            <Button type="button" data-testid="edit-relationship-save-btn" disabled={busy} onClick={saveRelationshipType}
                                className="rounded-full bg-brand-action hover:bg-brand-actionHover">Save</Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
